import { LicenceState, PlanType } from 'dy-frontend-http-repository/lib/data/enums';
import { CollectionResource } from 'dy-frontend-http-repository/lib/data/resources';
import {
    ClientUserListItemResource,
    LicenceResource,
} from 'dy-frontend-http-repository/lib/modules/ClientUser/resources';
import countriesData from 'dy-frontend-shared/lib/data/consts/countriesData';
import moment from 'moment';

// Pure helpers behind the Clients page "Export Contacts" button. Covered by
// ./clientContactsExport.test.ts (compiled with the repo TypeScript compiler and run on Node).

// Columns of the exported file, in the exact order the export must produce them
export const clientContactsCsvHeader = [
    'First Name',
    'Last Name',
    'Email',
    'Plan (Tier)',
    'Plan Status',
    'Cancelled / Paused Date',
    'Company Name',
    'Registration Date',
    'Country',
    'Timezone',
    'Website',
    'Type of Business',
    'Phone Number',
    'City',
    'Address 1',
    'Address 2',
    'About',
];

// Clients are fetched with the regular paginated list endpoint, so the export walks every page
export const clientContactsExportPageSize = 100;

// Safety cap, so a misbehaving paginator can never spin forever (100 * 500 = 50 000 clients)
export const clientContactsExportMaxPages = 500;

// The only accounts the "Export Contacts" button is shown to, lower cased
export const clientContactsExportAllowedEmails = ['sherwin@dotyeti.com', 'derek@dotyeti.com', 'alex@digitalwander.com'];

// Exact full email match, so plus addresses, subdomains and lookalike domains never pass
export const canExportClientContacts = (email: string | null | undefined): boolean => {
    if (!email) {
        return false;
    }

    return clientContactsExportAllowedEmails.includes(email.trim().toLowerCase());
};

export type ClientContactsPageFetcher = (pagination: {
    limit: number;
    offset: number;
}) => Promise<CollectionResource<ClientUserListItemResource, {}>>;

// Legacy plan titles and their current tier names, plus the current names themselves so already
// migrated titles are canonicalized to a single casing
const planTitleTierNames: { [lowerCasedPlanTitle: string]: string } = {
    basic: 'Base',
    premium: 'Scale',
    royal: 'Summit',
    enlightened: 'Apex',
    base: 'Base',
    scale: 'Scale',
    summit: 'Summit',
    apex: 'Apex',
};

// Known tiers come first in this order, anything else is appended alphabetically
const tierNameOrder = ['Base', 'Scale', 'Summit', 'Apex'];

// A cell starting with one of these can be executed as a formula by a spreadsheet application
const formulaInjectionCharacters = ['=', '+', '-', '@', '\t', '\r'];

// UTF-8 byte order mark, so Excel opens the file as UTF-8 instead of guessing an ANSI code page
const csvByteOrderMark = String.fromCharCode(0xfeff);

// Status labels of the Plan Status column, in the order they are listed in
const licenceStateStatusLabels: { state: LicenceState; label: string }[] = [
    { state: LicenceState.ACTIVE, label: 'Active' },
    { state: LicenceState.SUSPENDED, label: 'Paused' },
    { state: LicenceState.SHUTDOWN, label: 'Cancelled' },
];

// Milliseconds of a valid ISO timestamp, null for a missing or malformed one
const parseUtcTimestamp = (value: string | null | undefined): number | null => {
    if (!value) {
        return null;
    }

    const date = moment.utc(value, moment.ISO_8601);

    return date.isValid() ? date.valueOf() : null;
};

// The USER licences all three plan columns are derived from: the current ones when the client has
// any, otherwise the licences of the most recent cancellation. A client whose only history has no
// usable shutdown date reports nothing at all instead of an arbitrary plan.
export const selectClientPlanLicences = (client: ClientUserListItemResource): LicenceResource[] => {
    const userLicences = (client.licences ?? []).filter((licence) => licence.plan.type === PlanType.USER);

    const currentLicences = userLicences.filter((licence) => licence.state !== LicenceState.SHUTDOWN);
    if (currentLicences.length > 0) {
        return currentLicences;
    }

    const shutdownTimestamps = userLicences
        .map((licence) => parseUtcTimestamp(licence.shutdown_at))
        .filter((timestamp): timestamp is number => timestamp !== null);

    if (shutdownTimestamps.length === 0) {
        return [];
    }

    const latestShutdownAt = shutdownTimestamps.reduce((latest, timestamp) => Math.max(latest, timestamp));

    return userLicences.filter((licence) => parseUtcTimestamp(licence.shutdown_at) === latestShutdownAt);
};

// Plan (Tier) column: the selected plans, mapped to current tier names, deduped and ordered
export const resolveClientPlanTierLabel = (client: ClientUserListItemResource): string => {
    const tierNames: string[] = [];

    selectClientPlanLicences(client).forEach((licence) => {
        const planTitle = (licence.plan.title ?? '').trim();
        if (planTitle.length === 0) {
            return;
        }

        const tierName = planTitleTierNames[planTitle.toLowerCase()] ?? planTitle;
        if (tierNames.indexOf(tierName) === -1) {
            tierNames.push(tierName);
        }
    });

    tierNames.sort((a, b) => {
        const aIndex = tierNameOrder.indexOf(a);
        const bIndex = tierNameOrder.indexOf(b);

        if (aIndex !== -1 || bIndex !== -1) {
            if (aIndex === -1) {
                return 1;
            }

            if (bIndex === -1) {
                return -1;
            }

            return aIndex - bIndex;
        }

        const aLowerCased = a.toLowerCase();
        const bLowerCased = b.toLowerCase();

        if (aLowerCased !== bLowerCased) {
            return aLowerCased < bLowerCased ? -1 : 1;
        }

        return (
            a < b ? -1
            : a > b ? 1
            : 0
        );
    });

    return tierNames.join('; ');
};

// Plan Status column: the states of the selected plans, deduped and always Active, Paused, Cancelled
export const resolveClientPlanStatusLabel = (client: ClientUserListItemResource): string => {
    const licences = selectClientPlanLicences(client);

    return licenceStateStatusLabels
        .filter(({ state }) => licences.some((licence) => licence.state === state))
        .map(({ label }) => label)
        .join('; ');
};

// Cancelled / Paused Date column: when the selected plans were paused or cancelled, oldest first.
// An active plan has no such date, and an unusable one is left out instead of exported as garbage.
export const resolveClientPlanDateLabel = (client: ClientUserListItemResource): string => {
    const dates: string[] = [];

    selectClientPlanLicences(client).forEach((licence) => {
        const date =
            licence.state === LicenceState.SUSPENDED ? formatUtcDate(licence.suspended_at)
            : licence.state === LicenceState.SHUTDOWN ? formatUtcDate(licence.shutdown_at)
            : '';

        if (date.length > 0 && dates.indexOf(date) === -1) {
            dates.push(date);
        }
    });

    // Fixed width YYYY-MM-DD, so the plain string order is the chronological one
    dates.sort();

    return dates.join('; ');
};

// Country column: same human readable label the client details page shows, raw value as fallback
export const resolveClientCountryLabel = (client: ClientUserListItemResource): string => {
    const countryCode = client.address_country ?? '';

    return countriesData[countryCode]?.countryName ?? countryCode;
};

// Registration date column: the UTC calendar date, never the date of the exporting browser
export const formatUtcDate = (value: string | null | undefined): string => {
    if (!value) {
        return '';
    }

    const date = moment.utc(value, moment.ISO_8601);

    return date.isValid() ? date.format('YYYY-MM-DD') : '';
};

// A leading apostrophe keeps spreadsheet applications from evaluating a cell as a formula
export const escapeCsvCell = (value: string | null | undefined): string => {
    const rawValue = value ?? '';
    const safeValue = formulaInjectionCharacters.indexOf(rawValue.charAt(0)) === -1 ? rawValue : `'${rawValue}`;

    return `"${safeValue.split('"').join('""')}"`;
};

export const buildClientContactsCsvCells = (client: ClientUserListItemResource): string[] => {
    return [
        client.first_name,
        client.last_name,
        client.email,
        resolveClientPlanTierLabel(client),
        resolveClientPlanStatusLabel(client),
        resolveClientPlanDateLabel(client),
        client.company_name,
        formatUtcDate(client.created_at),
        resolveClientCountryLabel(client),
        client.timezone_name,
        client.website,
        client.company_business_type,
        client.phone,
        client.address_city,
        client.address_first_line,
        client.address_second_line,
        client.bio,
    ];
};

// RFC-4180: CRLF separated records, every field quoted, leading BOM for Excel
export const buildClientContactsCsv = (clients: ClientUserListItemResource[]): string => {
    const records = [clientContactsCsvHeader].concat(clients.map(buildClientContactsCsvCells));

    return csvByteOrderMark + records.map((cells) => cells.map(escapeCsvCell).join(',')).join('\r\n');
};

export const buildClientContactsCsvFileName = (now: Date = new Date()): string => {
    return `dotyeti-client-contacts-${moment.utc(now).format('YYYY-MM-DD')}.csv`;
};

// Walks every page of the client list. Anything that could produce a partial file (a page without
// pagination data, a known total that does not match what was collected, an empty page while more
// are promised, or the page cap) throws, so the export fails closed instead of writing an incomplete
// file. Some existing list endpoints intentionally omit the total; their has_more flag remains the
// authoritative completion signal.
export const collectAllClientContacts = async (
    fetchPage: ClientContactsPageFetcher,
    options: { pageSize?: number; maxPages?: number } = {}
): Promise<ClientUserListItemResource[]> => {
    const pageSize = options.pageSize ?? clientContactsExportPageSize;
    const maxPages = options.maxPages ?? clientContactsExportMaxPages;

    const clients: ClientUserListItemResource[] = [];
    let expectedTotal: number | null = null;
    let fetchedPages = 0;
    let offset = 0;
    let hasMore = true;

    while (hasMore) {
        if (fetchedPages >= maxPages) {
            throw new Error(`Client contacts export reached the ${maxPages} page safety cap`);
        }

        const collection = await fetchPage({ limit: pageSize, offset });
        fetchedPages += 1;

        const paginator = collection.paginator;
        if (!paginator) {
            throw new Error('Client contacts export received a page without pagination data');
        }

        const items = collection.items ?? [];
        items.forEach((item) => clients.push(item));

        if (paginator.total !== null) {
            expectedTotal = paginator.total;
        }
        hasMore = paginator.has_more;

        if (hasMore && items.length === 0) {
            throw new Error('Client contacts export received an empty page while more clients were expected');
        }

        // Advance by what the API actually returned, in case it caps the requested limit
        offset += items.length;
    }

    if (expectedTotal !== null && clients.length !== expectedTotal) {
        throw new Error(`Client contacts export fetched ${clients.length} of ${expectedTotal} clients`);
    }

    return clients;
};
